import { Trash2, GripVertical } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import type { NewsletterCard, LintFinding } from "@shared/newsletterTypes";

/**
 * One editable deal card.
 *
 * Every field is editable because the auto-fill is a starting point, not an
 * answer: blurbs seed from companies.description, which frequently contains
 * the client's own name. The lint flags that; a human rewrites it here.
 */
export function CardEditor({
  card, index, findings, onChange, onProjectNameCommit, onRemove, onMove,
}: {
  card: NewsletterCard;
  index: number;
  findings: LintFinding[];
  onChange: (next: NewsletterCard) => void;
  /** Persist the typed name as this client's project name, so the next issue pre-fills it. */
  onProjectNameCommit?: (value: string) => void;
  onRemove: () => void;
  onMove: (direction: -1 | 1) => void;
}) {
  const errors = findings.filter((f) => f.cardIndex === index && f.level === "error");
  const warnings = findings.filter((f) => f.cardIndex === index && f.level === "warning");
  const set = <K extends keyof NewsletterCard>(key: K, value: NewsletterCard[K]) =>
    onChange({ ...card, [key]: value });
  // Scoped per card so ids stay unique across every CardEditor on the page.
  const fieldId = (name: string) => `card-${index}-${name}`;

  return (
    <div className={cn("border rounded-lg p-4 space-y-3", errors.length && "border-destructive")}>
      <div className="flex items-center gap-2">
        <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
        <div className="flex-1">
          <Label htmlFor={fieldId("project")} className="sr-only">Project name</Label>
          <Input
            id={fieldId("project")}
            value={card.projectName ?? ""}
            onChange={(e) => set("projectName", e.target.value || null)}
            onBlur={(e) => onProjectNameCommit?.(e.target.value.trim())}
            placeholder="Project name, e.g. Project Falcon"
            className="font-semibold"
            aria-invalid={!card.projectName?.trim() || undefined}
          />
        </div>
        <div className="flex-1">
          {/* Visually the uppercase, bold headline already reads as this
              card's title; a visible label above it would just repeat that
              in a single-row header that has no room to stack one. */}
          <Label htmlFor={fieldId("headline")} className="sr-only">Headline</Label>
          <Input
            id={fieldId("headline")}
            value={card.headline}
            onChange={(e) => set("headline", e.target.value)}
            placeholder="HOME HEALTH · SOUTHWEST"
            className="uppercase tracking-wide text-xs"
          />
        </div>
        <Button variant="ghost" size="icon" onClick={() => onMove(-1)} aria-label="Move up">↑</Button>
        <Button variant="ghost" size="icon" onClick={() => onMove(1)} aria-label="Move down">↓</Button>
        <Button variant="ghost" size="icon" onClick={onRemove} aria-label="Remove deal">
          <Trash2 className="w-4 h-4 text-destructive" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor={fieldId("revenue")} className="text-xs text-muted-foreground">Revenue</Label>
          <Input
            id={fieldId("revenue")}
            value={card.revenue ?? ""}
            onChange={(e) => set("revenue", e.target.value || null)}
            placeholder="e.g. $12.4M"
          />
        </div>
        <div>
          <Label htmlFor={fieldId("ebitda")} className="text-xs text-muted-foreground">EBITDA</Label>
          <Input
            id={fieldId("ebitda")}
            value={card.ebitda ?? ""}
            onChange={(e) => set("ebitda", e.target.value || null)}
            placeholder="e.g. $2.8M"
          />
        </div>
      </div>

      <div>
        <Label htmlFor={fieldId("blurb")} className="text-xs text-muted-foreground">Blurb</Label>
        <Textarea
          id={fieldId("blurb")}
          value={card.blurb}
          onChange={(e) => set("blurb", e.target.value)}
          rows={3}
          placeholder="Two or three lines. No company name, no city."
        />
      </div>

      <div>
        <Label htmlFor={fieldId("bullets")} className="text-xs text-muted-foreground">Bullets</Label>
        <Textarea
          id={fieldId("bullets")}
          value={card.bullets.join("\n")}
          onChange={(e) => set("bullets", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
          rows={2}
          placeholder="Optional bullets, one per line"
        />
      </div>

      {errors.map((f, i) => (
        <p key={`e${i}`} className="text-sm text-destructive font-medium">{f.message}</p>
      ))}
      {warnings.map((f, i) => (
        <p key={`w${i}`} className="text-sm text-amber-600 dark:text-amber-400">{f.message}</p>
      ))}
    </div>
  );
}
